import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import config from 'config'
import { TextInput } from '../index'
import request from '@/modules/base/util/request'
import styles from './zipCode-input.css'
import { useFormikContext } from 'formik'
import lang from '@/lang'
import clsx from 'clsx'

const ZipCodeInput = (props) => {
  const cityRef = useRef()
  const {
    cityLabel = lang.cityLabel,
    customClass,
    hasPlaceholder,
    isInline = false,
    zipCodeLabel = lang.zipLabel,
    useFormik,
    readOnly,
    hideErrorMessage = false,
    customPlaceholder,
    customStyles = {},
    zipCode: zipCodeProp = '',
    city: cityProp = '',
    dataQsId,
    required,
    onChange,
    customClassLabel,
  } = props
  const [state, setState] = useState({
    zipCode: zipCodeProp,
    city: cityProp,
    errorMessage: '',
  })
  const { zipCode, city } = state

  let formikContext = null

  if (useFormik) {
    formikContext = useFormikContext()
  }

  useEffect(() => {
    setState({
      zipCode: zipCodeProp,
      city: cityProp,
      errorMessage: '',
    })
  }, [zipCodeProp, cityProp])

  useEffect(() => {
    onChange && onChange(state)
  }, [state])

  const handleTextInputChange = (value, name) => {
    const errorState = { errorMessage: '' }

    if (required && !value) {
      errorState.errorMessage = lang.emptyRequiredFieldErrorMessage
    }

    // based on the latest state: the browser's autofill fills PLZ and Ort before the next render
    setState(previous => ({ ...previous, [name]: value, ...errorState }))
  }

  const handleZipCodeBlur = (value) => {
    const { city: currentCity } = state

    if (required && !value) {
      setState(previous => ({ ...previous, errorMessage: lang.emptyRequiredFieldErrorMessage }))
    }
    if (currentCity || !value) {
      return
    }

    request(`${config.getCityToZipCode}/${value}`, { auth: true })
      .then((response) => {
        const { body: { data } } = response
        const options = []
        data.forEach(({ name, zipCodes }) => {
          zipCodes.forEach(({ zipCode }) => {
            options.push({ label: `${zipCode} ${name}`, value: name, zip: zipCode })
          })
        })

        const changes = { zipCode: value, options, city: undefined }

        if (data.length === 1) {
          const [{ name }] = data
          changes.city = name
          changes.options = [{ label: name, value: name, zip: value }]
          if (formikContext) {
            formikContext.setFieldValue('city', name)
          }
        }

        setState(previous => ({ ...previous, ...changes }))
      })
  }

  const handleCityBlur = (value) => {
    if (required && !value) {
      setState(previous => ({ ...previous, errorMessage: lang.emptyRequiredFieldErrorMessage }))
    }
  }

  const inputClass = isInline ? styles.inline : null
  const zipCodePlaceholder = hasPlaceholder ? lang.zipLabel : ''
  const cityPlaceholder = hasPlaceholder ? lang.cityLabel : ''

  return (
    <div className={`${styles.container} ${customClass || ''}`} data-qs-id={dataQsId}>
      <div className={styles.innerContainer}>
        <div className={clsx(styles.zipCodeInput, customStyles?.zipCodeInput)}>
          <TextInput
            name='zipCode'
            value={zipCode}
            customClass={inputClass}
            label={zipCodeLabel}
            errorMessage={state.errorMessage}
            hideErrorMessage
            readOnly={readOnly}
            placeholder={customPlaceholder || zipCodePlaceholder}
            onBlur={handleZipCodeBlur}
            onChange={handleTextInputChange}
            useFormik={useFormik}
            customClassLabel={customClassLabel}
          />
        </div>
        <div className={clsx(styles.cityInput, customStyles?.cityInput)}>
          <TextInput
            ref={cityRef}
            name='city'
            value={city}
            label={cityLabel}
            errorMessage={state.errorMessage}
            hideErrorMessage
            readOnly={readOnly}
            placeholder={customPlaceholder || cityPlaceholder}
            customClass={inputClass}
            onBlur={handleCityBlur}
            onChange={handleTextInputChange}
            useFormik={useFormik}
            customClassLabel={customClassLabel}
          />
        </div>
      </div>
      {state.errorMessage && !hideErrorMessage ? <div className={styles.error}>{state.errorMessage}</div> : ''}
    </div>
  )

}

ZipCodeInput.propTypes = {
  zipCode: PropTypes.string,
  city: PropTypes.string,
  isInline: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  cityLabel: PropTypes.string,
  zipCodeLabel: PropTypes.string,
  customClass: PropTypes.string,
  hasPlaceholder: PropTypes.bool,
  useFormik: PropTypes.bool,
  readOnly: PropTypes.bool,
  customPlaceholder: PropTypes.string,
  dataQsId: PropTypes.string.isRequired,
}

export default ZipCodeInput
